<script setup lang="ts">
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";
import type { Sticker } from "~/types";
import { ChatRules } from "#components";
import { useChatStore } from "~/stores/chat/store";

const inputText = ref("");

const props = defineProps({
  connection: {
    type: Object as PropType<WebSocket | null>,
    required: true,
  },
  readyState: {
    type: Number,
    default: 0,
  },
  initChat: {
    type: Function,
    required: true,
  },
});

const dialog = useDialog();

const chatStore = useChatStore();
const { stickers, chatStatus, tabDisabled } = storeToRefs(chatStore);

onMounted(() => {
  props.initChat();
});

function sendMessage() {
  if (
    props.connection &&
    props.connection.readyState === 1 &&
    inputText.value &&
    inputText.value.length < 201
  ) {
    props.connection.send(JSON.stringify({ message: inputText.value }));
    inputText.value = "";
  }
}

function onSelectEmoji(emoji: any) {
  inputText.value = inputText.value + emoji.i;
}

const disabledGroups = [
  "smileys_people",
  "animals_nature",
  "food_drink",
  "activities",
  "travel_places",
  "objects",
  "symbols",
  "flags",
];

const additionalGroups = {
  select_emoji: [
    { n: ["grinning face", "grinning"], u: "1f600" },
    {
      n: ["rolling on the floor laughing", "rolling_on_the_floor_laughing"],
      u: "1f923",
    },
    {
      n: ["winking face", "wink"],
      u: "1f609",
    },
    {
      n: ["hugging face", "hugging_face"],
      u: "1f917",
    },
    {
      n: [
        "smiling face with smiling eyes and hand covering mouth",
        "face_with_hand_over_mouth",
        "smiling_face_with_smiling_eyes_and_hand_covering_mouth",
      ],
      u: "1f92d",
    },
    {
      n: [
        "face with finger covering closed lips",
        "shushing_face",
        "face_with_finger_covering_closed_lips",
      ],
      u: "1f92b",
    },
    {
      n: ["thinking face", "thinking_face"],
      u: "1f914",
    },
    {
      n: ["face without mouth", "no_mouth"],
      u: "1f636",
    },
    {
      n: ["face with monocle", "face_with_monocle"],
      u: "1f9d0",
    },
    {
      n: ["confounded face", "confounded"],
      u: "1f616",
    },
    {
      n: ["victory hand", "v"],
      u: "270c-fe0f",
      v: ["270c-1f3fb", "270c-1f3fc", "270c-1f3fd", "270c-1f3fe", "270c-1f3ff"],
    },
    {
      n: [
        "hand with index and middle fingers crossed",
        "crossed_fingers",
        "hand_with_index_and_middle_fingers_crossed",
      ],
      u: "1f91e",
      v: [
        "1f91e-1f3fb",
        "1f91e-1f3fc",
        "1f91e-1f3fd",
        "1f91e-1f3fe",
        "1f91e-1f3ff",
      ],
    },
  ],
};

const emojiPickerRef = ref();
const stickerPickerRef = ref();
const toggleEmojiPicker = (event: Event) => {
  emojiPickerRef.value.toggle(event);
};

const toggleStickerPicker = (event: Event) => {
  stickerPickerRef.value.toggle(event);
};

function sendSticker(sticker: Sticker) {
  if (props.connection && props.connection.readyState === 1) {
    props.connection.send(
      JSON.stringify({ message: "sticker:" + sticker.sticker_id }),
    );
    stickerPickerRef.value.hide();
  }
}
</script>

<template>
  <div>
    <Popover
      ref="emojiPickerRef"
      :pt="{ root: 'before:!hidden after:!hidden bg-white', content: '!p-0 ' }"
      :pt-options="{ mergeProps: true }"
    >
      <div>
        <div class="p-1 text-right">
          <font-awesome-icon
            :icon="['fas', 'xmark']"
            class="cursor-pointer"
            @click="toggleEmojiPicker"
          />
        </div>
        <div>
          <EmojiPicker
            class="!border-none"
            :native="true"
            :disabled-groups="disabledGroups"
            :additional-groups="additionalGroups"
            disable-skin-tones
            @select="onSelectEmoji"
          />
        </div>
      </div>
    </Popover>

    <Popover
      ref="stickerPickerRef"
      :pt="{ root: 'before:!hidden after:!hidden bg-white', content: '!p-0 ' }"
      :pt-options="{ mergeProps: true }"
    >
      <div>
        <div class="p-1 text-right">
          <font-awesome-icon
            :icon="['fas', 'xmark']"
            class="cursor-pointer"
            @click="toggleStickerPicker"
          />
        </div>
        <div>
          <StickerPicker :stickers="stickers" @send-sticker="sendSticker" />
        </div>
      </div>
    </Popover>

    <div class="bg-surface-bg p-3 text-sm">
      <div v-if="chatStatus === 'live' && !tabDisabled">
        <div v-if="readyState === 3">
          <Button fluid @click="initChat()">
            <font-awesome-icon :icon="['fas', 'rotate']" class="mr-2" />
            Reconnect
          </Button>
        </div>
        <div v-else>
          <div class="flex gap-3">
            <div class="relative flex-grow items-center">
              <PgInputText
                v-model="inputText"
                name="email"
                :disabled="readyState !== 1"
                type="email"
                fluid
                :placeholder="
                  readyState === 0 ? 'Connecting ...' : 'Type your message'
                "
                @keydown.enter="sendMessage"
              />
              <div
                class="absolute right-7 top-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center"
                @click="toggleEmojiPicker"
              >
                <font-awesome-icon
                  :icon="['fas', 'face-smile']"
                  class="text-lg text-[#E09BFD]"
                />
              </div>

              <div
                class="absolute right-1 top-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center"
                @click="toggleStickerPicker"
              >
                <font-awesome-icon
                  :icon="['far', 'note-sticky']"
                  class="text-lg text-[#E09BFD]"
                />
              </div>
            </div>
            <div
              class="flex w-12 cursor-pointer items-center justify-center rounded-md bg-[#9E23D2] hover:bg-primary-600"
              @click="sendMessage"
            >
              <font-awesome-icon
                :icon="['fas', 'paper-plane-top']"
                class="text-lg text-white"
              />
            </div>
          </div>
          <div v-if="inputText.length > 200" class="mt-2 text-xs text-red-500">
            Message should be less than 200 characters
          </div>
        </div>
      </div>
      <div v-else class="text-[#ff760d]">
        <div v-if="tabDisabled">Chat window deactivated</div>
        <div v-else-if="chatStatus === 'no_record'">
          This chat has not been started yet
        </div>
        <div v-else>This chat has been {{ chatStatus }}</div>
      </div>
      <div class="mt-2 flex items-center justify-between">
        <div
          class="cursor-pointer font-bold text-[#E09BFD]"
          @click="
            () => {
              dialog.open(ChatRules, {
                props: {
                  modal: true,
                },
              });
            }
          "
        >
          Chat Rules
        </div>
        <div
          v-if="chatStatus === 'live' && !tabDisabled"
          class="mb-2 text-surface-300"
        >
          {{ inputText.length }}/200
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
